/**
 * MAPA EXTERNO DE DISTRIBUIÇÃO
 * Página autenticada para visualização do mapa com filtros hierárquicos
 * Acesso: /mapa-externo (após autenticação com CPF)
 */

import { useEffect, useRef, memo, useState } from 'react'
import { MapContainer, TileLayer, Marker as LeafletMarker, Popup, useMap, CircleMarker } from 'react-leaflet'
import { basemapUrl, BASEMAP_ATTRIB } from './lib/basemap'
import { DivIcon } from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Tipo para os dados de ativas
interface Ativa {
  codigo: string
  nome: string
  tipo_consultor: '1' | '2' | '3' // '1' = Empresário, '2' = Líder, '3' = Consultora
  cep?: string
  latitude: number
  longitude: number
  semana?: string
}

// Tipo para dados do usuário autenticado
interface UserData {
  cpf: string
  codigo: string
  nome: string
  tipo_consultor: '1' | '2' | '3'
}

// Ícone para Líderes (vermelho)
const liderIcon = new DivIcon({
  html: `<div style="color: #dc2626;">
    <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" stroke="#888888" stroke-width="0.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
  className: 'leaflet-marker-lider'
})

// Ícone para Consultoras (azul)
const consultoraIcon = new DivIcon({
  html: `<div style="color: #3b82f6;">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="#888888" stroke-width="0.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
  className: 'leaflet-marker-consultora'
})

// Ícone para Empresários (roxo, maior)
const empresarioIcon = new DivIcon({
  html: `<div style="color: #8B5CF6;">
    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" stroke="#888888" stroke-width="0.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
  className: 'leaflet-marker-empresario'
})

// Centro do MS
const MS_CENTER: [number, number] = [-20.5, -54.5]

// Componente para ajustar bounds
const MapBoundsAdjuster = memo(({ markers }: { markers: Array<{ latitude: number; longitude: number }> }) => {
  const map = useMap()
  const adjustedRef = useRef(false)

  useEffect(() => {
    if (markers.length > 0 && !adjustedRef.current) {
      const bounds = markers.map(m => [m.latitude, m.longitude] as [number, number])
      if (bounds.length === 1) {
        map.setView(bounds[0], 12)
      } else if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 })
      }
      adjustedRef.current = true
    }
  }, [markers, map])

  return null
})

// Componente principal do mapa externo
const MapaExterno = () => {
  const [ativas, setAtivas] = useState<Ativa[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState({ total: 0, empresarios: 0, lideres: 0, consultoras: 0, semana: '' })
  const [showHeatmap, setShowHeatmap] = useState(false)
  const [showEmpresarios, setShowEmpresarios] = useState(true)
  const [showLideres, setShowLideres] = useState(true)
  const [showConsultoras, setShowConsultoras] = useState(true)
  const [userData, setUserData] = useState<UserData | null>(null)

  // Verificar autenticação e buscar dados
  useEffect(() => {
    console.log('[MAPA EXTERNO] Componente montado - verificando autenticação...')

    const token = sessionStorage.getItem('mapaToken')
    const userStr = sessionStorage.getItem('mapaUser')

    if (!token || !userStr) {
      console.log('[MAPA EXTERNO] Não autenticado - redirecionando...')
      window.location.href = '/mapa'
      return
    }

    const user: UserData = JSON.parse(userStr)
    setUserData(user)

    const fetchAtivas = async () => {
      try {
        const response = await fetch('/api/public/mapa/ativas-filtradas', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.status === 401) {
          // Token inválido - redirecionar para login
          sessionStorage.removeItem('mapaToken')
          sessionStorage.removeItem('mapaUser')
          window.location.href = '/mapa'
          return
        }

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`)
        }

        const data = await response.json()

        console.log('[MAPA EXTERNO] Dados recebidos:', data.total, 'ativas')
        setAtivas(data.ativas || [])
        setStats({
          total: data.total || 0,
          empresarios: data.empresarios || 0,
          lideres: data.lideres || 0,
          consultoras: data.consultoras || 0,
          semana: data.semana || ''
        })
        setError(null)
      } catch (err) {
        console.error('[MAPA EXTERNO] Erro ao buscar dados:', err)
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados')
      } finally {
        setLoading(false)
      }
    }

    fetchAtivas()

    return () => console.log('[MAPA EXTERNO] Componente desmontado')
  }, [])

  const handleLogout = () => {
    sessionStorage.removeItem('mapaToken')
    sessionStorage.removeItem('mapaUser')
    window.location.href = '/mapa'
  }

  // Função para obter o tipo de usuário em texto
  const getTipoUsuario = () => {
    if (!userData) return ''
    switch (userData.tipo_consultor) {
      case '1': return 'Empresário'
      case '2': return 'Líder'
      case '3': return 'Consultora'
      default: return ''
    }
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-slate-900 text-white">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-[#00524E] to-[#007a73] shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/tw1.png" alt="Logo" className="h-10 w-auto" />
            <div>
              <h1 className="text-xl font-bold">Mapa de Distribuição</h1>
              <p className="text-xs text-white/70">
                {stats.semana ? `Semana ${stats.semana}` : 'Mato Grosso do Sul'} • {getTipoUsuario()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Dados do usuário */}
            {userData && (
              <div className="text-right text-sm text-white/90">
                <div className="font-semibold">{userData.nome}</div>
                <div className="text-xs text-white/70">Cód: {userData.codigo}</div>
              </div>
            )}
            {/* Status */}
            <div className="text-sm text-white/80">
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                  <span>Carregando...</span>
                </div>
              ) : error ? (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full" />
                  <span>Erro</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span>{stats.total} ativas</span>
                </div>
              )}
            </div>
            {/* Botão de Logout */}
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* Mapa */}
      <div className="flex-1 relative">
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-800/90 z-[1001]">
            <div className="text-center p-6">
              <div className="text-red-400 text-lg mb-2">Erro ao carregar dados</div>
              <div className="text-slate-400 text-sm">{error}</div>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-[#00524E] hover:bg-[#007a73] rounded-lg text-sm"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        <MapContainer
          center={MS_CENTER}
          zoom={7}
          style={{ width: '100%', height: '100%' }}
          className="z-0"
        >
          {/* Sai do tile.openstreetmap.org: a Tile Usage Policy da OSMF
              desaconselha uso comercial pesado, e o mapa é público. */}
          <TileLayer
            attribution={BASEMAP_ATTRIB}
            url={basemapUrl(false)}
            maxZoom={20}
          />

          {ativas.length > 0 && (
            <MapBoundsAdjuster markers={ativas} />
          )}

          {/* Efeito de Mapa de Calor usando CircleMarker */}
          {showHeatmap && ativas
            .filter(a =>
              (a.tipo_consultor === '1' && showEmpresarios) ||
              (a.tipo_consultor === '2' && showLideres) ||
              (a.tipo_consultor === '3' && showConsultoras)
            )
            .map((ativa) => (
            <CircleMarker
              key={`heat-${ativa.codigo}`}
              center={[ativa.latitude, ativa.longitude]}
              radius={ativa.tipo_consultor === '1' ? 30 : ativa.tipo_consultor === '2' ? 25 : 18}
              pathOptions={{
                color: 'transparent',
                fillColor: ativa.tipo_consultor === '1' ? '#8B5CF6' : ativa.tipo_consultor === '2' ? '#ff4444' : '#ff8844',
                fillOpacity: 0.4,
                weight: 0
              }}
            />
          ))}

          {/* Marcadores de Consultoras (primeiro, para ficarem embaixo) */}
          {showConsultoras && ativas.filter(a => a.tipo_consultor === '3').map((ativa) => (
            <LeafletMarker
              key={`marker-cons-${ativa.codigo}`}
              position={[ativa.latitude, ativa.longitude]}
              icon={consultoraIcon}
            >
              <Popup>
                <div className="text-sm min-w-[150px]">
                  <div className="font-semibold text-blue-600">{ativa.nome}</div>
                  <div className="text-xs text-gray-500 mt-1">Consultora</div>
                  {ativa.cep && (
                    <div className="text-xs text-gray-600 mt-1">
                      CEP: {ativa.cep}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    Código: {ativa.codigo}
                  </div>
                </div>
              </Popup>
            </LeafletMarker>
          ))}

          {/* Marcadores de Líderes (camada média) */}
          {showLideres && ativas.filter(a => a.tipo_consultor === '2').map((ativa) => (
            <LeafletMarker
              key={`marker-lider-${ativa.codigo}`}
              position={[ativa.latitude, ativa.longitude]}
              icon={liderIcon}
            >
              <Popup>
                <div className="text-sm min-w-[150px]">
                  <div className="font-semibold text-red-600">{ativa.nome}</div>
                  <div className="text-xs text-gray-500 mt-1">Líder</div>
                  {ativa.cep && (
                    <div className="text-xs text-gray-600 mt-1">
                      CEP: {ativa.cep}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    Código: {ativa.codigo}
                  </div>
                </div>
              </Popup>
            </LeafletMarker>
          ))}

          {/* Marcadores de Empresários (topo - maior hierarquia) */}
          {showEmpresarios && ativas.filter(a => a.tipo_consultor === '1').map((ativa) => (
            <LeafletMarker
              key={`marker-emp-${ativa.codigo}`}
              position={[ativa.latitude, ativa.longitude]}
              icon={empresarioIcon}
            >
              <Popup>
                <div className="text-sm min-w-[150px]">
                  <div className="font-semibold text-purple-600">{ativa.nome}</div>
                  <div className="text-xs text-gray-500 mt-1">Empresário</div>
                  {ativa.cep && (
                    <div className="text-xs text-gray-600 mt-1">
                      CEP: {ativa.cep}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    Código: {ativa.codigo}
                  </div>
                </div>
              </Popup>
            </LeafletMarker>
          ))}
        </MapContainer>

        {/* Legenda e Controles */}
        <div className="absolute bottom-4 left-4 bg-white/95 text-gray-800 px-4 py-3 rounded-lg shadow-lg z-[1000]">
          {/* Toggle Mapa de Calor */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
            <span className="text-xs font-medium text-gray-700">Mapa de Calor</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowHeatmap(prev => !prev)
              }}
              className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                showHeatmap ? 'bg-[#00524E]' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                  showHeatmap ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="text-xs font-semibold mb-2">Camadas</div>
          <div className="flex flex-col gap-2 text-xs">
            {/* Empresário toggle */}
            {stats.empresarios > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowEmpresarios(prev => !prev) }}
                  className={`relative w-8 h-4 rounded-full transition-colors duration-200 flex-shrink-0 ${
                    showEmpresarios ? 'bg-[#8B5CF6]' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform duration-200 ${
                    showEmpresarios ? 'translate-x-4' : 'translate-x-0'
                  }`} />
                </button>
                <div className="w-4 h-4 flex items-center justify-center" style={{ opacity: showEmpresarios ? 1 : 0.3 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#8B5CF6">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <span style={{ opacity: showEmpresarios ? 1 : 0.4 }}>Empresários ({stats.empresarios})</span>
              </div>
            )}
            {/* Líder toggle */}
            {stats.lideres > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowLideres(prev => !prev) }}
                  className={`relative w-8 h-4 rounded-full transition-colors duration-200 flex-shrink-0 ${
                    showLideres ? 'bg-[#dc2626]' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform duration-200 ${
                    showLideres ? 'translate-x-4' : 'translate-x-0'
                  }`} />
                </button>
                <div className="w-4 h-4 flex items-center justify-center" style={{ opacity: showLideres ? 1 : 0.3 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#dc2626">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <span style={{ opacity: showLideres ? 1 : 0.4 }}>Líderes ({stats.lideres})</span>
              </div>
            )}
            {/* Consultora toggle */}
            {stats.consultoras > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowConsultoras(prev => !prev) }}
                  className={`relative w-8 h-4 rounded-full transition-colors duration-200 flex-shrink-0 ${
                    showConsultoras ? 'bg-[#3b82f6]' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform duration-200 ${
                    showConsultoras ? 'translate-x-4' : 'translate-x-0'
                  }`} />
                </button>
                <div className="w-4 h-4 flex items-center justify-center" style={{ opacity: showConsultoras ? 1 : 0.3 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#3b82f6">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <span style={{ opacity: showConsultoras ? 1 : 0.4 }}>Consultoras ({stats.consultoras})</span>
              </div>
            )}
            {showHeatmap && (
              <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                <div className="w-12 h-3 rounded" style={{ background: 'linear-gradient(to right, #00ff00, #ffff00, #ff8800, #ff0000)' }} />
                <span className="text-gray-500">Densidade</span>
              </div>
            )}
          </div>
        </div>

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 z-[1001]">
            <div className="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-[#00524E] border-t-transparent rounded-full animate-spin" />
              <span className="text-gray-700">Carregando mapa...</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-2 bg-slate-800 border-t border-slate-700 text-center text-xs text-slate-400">
        Use o scroll do mouse para zoom • Clique e arraste para mover
        {stats.semana && <span className="ml-4">• Semana: {stats.semana}</span>}
      </div>
    </div>
  )
}

export default MapaExterno
